import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import PercentileBars from './PercentileBars';
import { formatDuration, formatNumber } from './formatters';

const SLA_TARGET_S = 30;

const colorForP95 = (p95) => {
  if (p95 == null) return 'var(--text-tertiary)';
  if (p95 < 15) return '#10B981';
  if (p95 < SLA_TARGET_S) return '#F59E0B';
  return '#DC2626';
};

const HighlightKpi = ({ value, color, sub, label }) => (
  <Card className="metric-card h-100">
    <CardBody>
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 28,
          fontWeight: 700,
          color,
          transition: 'color 350ms ease',
        }}
      >
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{sub}</div>}
    </CardBody>
  </Card>
);

const LatencySection = ({ data }) => {
  const { t } = useTranslation();
  const proc = data?.processing_time_seconds || {};
  const totalP95 = Number(proc.total?.p95);
  const doneP95 = Number(proc.done?.p95);
  const failedP95 = Number(proc.failed?.p95);

  return (
    <Card className="mb-4 h-100">
      <CardHeader className="d-flex justify-content-between align-items-center">
        <span>
          <i className="fas fa-stopwatch me-2" style={{ color: '#3B82F6' }} />
          {t('Tiempo de procesamiento')}
        </span>
        <small style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>
          started_at → finished_at · {t('SLA objetivo p95')} &lt; {SLA_TARGET_S}s
        </small>
      </CardHeader>
      <CardBody>
        <Row className="g-3 mb-3">
          <Col xs="12" md="4">
            <HighlightKpi
              label="p95 total"
              value={isFinite(totalP95) ? formatDuration(totalP95) : '—'}
              color={colorForP95(totalP95)}
              sub={`${formatNumber(proc.total?.count)} ${t('jobs medidos')}`}
            />
          </Col>
          <Col xs="12" md="4">
            <HighlightKpi
              label="p95 done"
              value={isFinite(doneP95) ? formatDuration(doneP95) : '—'}
              color={colorForP95(doneP95)}
              sub={`mean ${formatDuration(proc.done?.mean)} · ${formatNumber(proc.done?.count)} jobs`}
            />
          </Col>
          <Col xs="12" md="4">
            <HighlightKpi
              label="p95 failed"
              value={isFinite(failedP95) ? formatDuration(failedP95) : '—'}
              color={colorForP95(failedP95)}
              sub={`mean ${formatDuration(proc.failed?.mean)} · ${formatNumber(proc.failed?.count)} jobs`}
            />
          </Col>
        </Row>

        <PercentileBars
          groups={proc}
          metrics={['mean', 'p50', 'p95', 'p99']}
          slaSeconds={SLA_TARGET_S}
          height={280}
        />

        <div
          style={{
            fontSize: 12,
            color: 'var(--text-tertiary)',
            marginTop: 8,
          }}
        >
          <i className="fas fa-info-circle me-1" />
          {t('Comparación entre jobs')} <b style={{ color: '#10B981' }}>{t('exitosos')}</b> {t('y')}{' '}
          <b style={{ color: '#DC2626' }}>{t('fallidos')}</b>. <code>max</code> {t('se omite del gráfico (siempre sesgado por outliers/zombies); aparece en el tooltip junto a')} <code>min</code>.
        </div>
      </CardBody>
    </Card>
  );
};

export default LatencySection;
